import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { useAuthContext } from './AuthContext';
import { UserService } from '../modules/configuracoes/usuario/Service';
import { useAppContext } from './AppContext';
const userService = new UserService();

interface EstabsContextProps {
    estabsUser: any[];
    estabSession: any;
    switchEstab: (estabId: string) => void;
    reloadEstab: () => void;
    // Cliente (sem perfil interno): estabelecimento escolhido neste dispositivo, sem trocar a sessao do servidor.
    isClientOnly: boolean;
    clientEstab: any;
    selectClientEstab: (estabId: string) => void;
}

const clientEstabStorageKey = (uid?: string) => `sf:client-estab:${uid || ''}`;

const readStoredClientEstab = (uid?: string) => {
    try {
        return window.localStorage.getItem(clientEstabStorageKey(uid)) || '';
    } catch {
        return '';
    }
};

const EstabsContext = createContext<EstabsContextProps | undefined>(undefined);

const EstabsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuthContext();
    const { errorSnackbar } = useAppContext();
    const [estabsUser, setEstabsUser] = useState<string[]>([]);
    const [estabSession, setEstabSession] = useState<{}>({});
    const [clientEstabId, setClientEstabId] = useState('');
    const isClientOnly = !!user?.client && !user?.admin && !user?.worker && !user?.root;

    useEffect(() => {
        setClientEstabId(readStoredClientEstab(user?.uid));
    }, [user?.uid]);

    // Escolhido salvo no dispositivo; se nao houver (ou nao estiver mais na lista), usa o primeiro.
    const clientEstab = useMemo(() => {
        if (!isClientOnly || !Array.isArray(estabsUser) || !estabsUser.length) return null;
        return (estabsUser as any[]).find((estab) => estab?._id === clientEstabId) || estabsUser[0];
    }, [clientEstabId, estabsUser, isClientOnly]);

    const selectClientEstab = (estabId: string) => {
        setClientEstabId(estabId);
        try {
            window.localStorage.setItem(clientEstabStorageKey(user?.uid), estabId);
        } catch {
            // Armazenamento indisponivel (ex.: modo privado): a escolha vale ate recarregar.
        }
    };

    const reloadEstab = () => {
        if (!!user) {
            userService.getEstabsUser().then(async (response) => {
                setEstabsUser(response?.data);

                const estab = response?.data?.find((e: any) => e._id === user?.estabIdSession);

                setEstabSession(estab);
            });
        }
    };

    useEffect(() => {
        reloadEstab();
    }, [user]);

    const switchEstab = (estabId: string) => {
        if (user?.estabIdSession !== estabId) {
            userService
                .switchEstab(estabId)
                .then((_) => {
                    window.location.reload();
                })
                .catch((e) => {
                    console.log(e);

                    errorSnackbar('Ocorreu um erro ao trocar de estabelecimento.');
                });
        }
    };

    return (
        <EstabsContext.Provider
            value={{
                switchEstab,
                estabsUser,
                estabSession,
                reloadEstab,
                isClientOnly,
                clientEstab,
                selectClientEstab,
            }}
        >
            {children}
        </EstabsContext.Provider>
    );
};

const useEstabsContext = () => {
    const context = useContext(EstabsContext);
    if (context === undefined) {
        throw new Error('useEstabsContext must be used within an EstabsProvider');
    }
    return context;
};

export { EstabsProvider, useEstabsContext };
